import { memo, useState, useEffect, FC } from 'react';
import { useTranslation } from 'react-i18next';
// Import UI Components
import { Modal, Button } from '@geneui/components';
// Import Components
import ButtonWithLoader from 'components/common/ButtonWithLoader';
import CECModalBody from './CECModalBody';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { AdministrationHttpService } from 'services/http';
// Import Constants
import { l } from 'constants/common';
// Import Enums
import { PageTypesEnum } from 'enums';

interface Props {
    type: string;
    isVisibleModal: boolean;
    onCancel: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onOk: (provider: any) => void;
    openedModalData?: {
        EmailProviderId: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    };
    isLoading: boolean;
}

const AdminProviderCECModal: FC<Props> = ({ isVisibleModal, onCancel, onOk, openedModalData, isLoading, type }) => {
    const { t } = useTranslation();

    const { doGetRequest } = useRequest();

    const [provider, setProvider] = useState({});
    const [isInitialProviderDataLoading, setIsInitialProviderDataLoading] = useState(false);

    const { getEmailProviderRequest } = useCancelableRequests({
        getEmailProviderRequest: AdministrationHttpService.getEmailProvider,
    });

    const getTitle = (type) => {
        switch (type) {
            case PageTypesEnum.create:
                return l.CreateProvider;
            case PageTypesEnum.edit:
                return l.EditProvider;
            case PageTypesEnum.clone:
                return l.CloneProvider;
            default:
                return '';
        }
    };

    const init = () => {
        if (type !== PageTypesEnum.create) {
            setIsInitialProviderDataLoading(true);
            doGetRequest(getEmailProviderRequest.request, {
                queryString: {
                    providerId: openedModalData?.EmailProviderId,
                },
                successCallback: (data) => {
                    if (type === PageTypesEnum.clone) {
                        setProvider({
                            ...data,
                            EmailProviderId: openedModalData?.EmailProviderId,
                            PartnerId: data.Partners[0].PartnerId,
                        });
                    } else {
                        setProvider({ ...data, EmailProviderId: openedModalData?.EmailProviderId });
                    }
                    setIsInitialProviderDataLoading(false);
                },
            });
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <Modal
            size="content-size"
            background="dark-background"
            position="center"
            title={t(getTitle(type))}
            className="crm-modal crm-set-goal-modal"
            appearance="default"
            closable={false}
            closeOnClickOutside={false}
            visible={isVisibleModal}
            onCancel={onCancel}
            footer={
                <>
                    <Button flexibility="default" color="default" appearance={'minimal'} onClick={onCancel}>
                        {t(l.Cancel)}
                    </Button>
                    <ButtonWithLoader
                        flexibility="default"
                        isLoading={isLoading}
                        color="primary"
                        onClick={() => onOk(provider)}
                    >
                        {t(l.Save)}
                    </ButtonWithLoader>
                </>
            }
        >
            <CECModalBody data={provider} setData={setProvider} type={type} isLoading={isInitialProviderDataLoading} />
        </Modal>
    );
};

export default memo(AdminProviderCECModal);
